import React, { FunctionComponent, useCallback, useEffect, useState, useMemo } from 'react';
import { PlayerBase, IThumbnail as IPlayerThumbnail } from '@24i/player-base';
import { usePlayerTimes } from '@24i/player-utils-react';
import { useThumbnails } from 'LocalGluons/components/PlayerContainer/hooks/useThumbnails';
import { Device } from 'DeviceUtils';
import { CONTAINER_WIDTH, ThumbnailContainer, ThumbnailAnchor, CONTAINER_HEIGHT } from './styled';
import { ThumbnailImage } from '../ThumbnailImage';
import { IThumbnailStyles } from '../../types';

const PERCENT = 100;
export interface IThumbnail {
    progress?: number;
    duration?: number;
    playerInstance: PlayerBase;
    styles?: IThumbnailStyles;
    isVisible?: boolean;
    className?: string;
}

export const Thumbnail: FunctionComponent<IThumbnail> = ({
    progress = 0,
    isVisible = false,
    className,
    styles = {},
    playerInstance
}) => {

    const [position, setPosition] = useState(null);
    const [nextThumbnail, setNextThumbnail] = useState<IPlayerThumbnail | null>(null);
    const { durationTime, loadedMetadata } = usePlayerTimes(playerInstance);
    const { spritesheet, imagesBase64, item } = useThumbnails();

    useEffect(() => {
        if (
            !playerInstance
            || !loadedMetadata
            || item?.data?.thumbnailsEnabled !== true
        ) return;
        const numericPercentage = progress / PERCENT;
        setPosition(numericPercentage);
    }, [isVisible, progress, playerInstance, loadedMetadata, durationTime, item]);

    const doLoadThumbnail = useCallback(async () => {
        let length;
        let end = 0;

        if (item?.modelType === 'recording' && playerInstance?.duration) {
            length = playerInstance.duration / 1000;
        } else if (typeof item?.getEndTime === 'function' && typeof item?.getStartTime === 'function') {
            length = (item?.getEndTime() - item?.getStartTime()) / 1000;
            end = Date.now() - item?.getStartTime();
        } else if (typeof item?.getDuration === 'function') {
            length = item?.getDuration();
        } else {
            return;
        }
        let loadedThumbnail: IPlayerThumbnail | null;

        let thumbnailNumber = Math.floor((length * position) / 10);
        if (Device.isIos) {
            // ios time correction
            const n = playerInstance?.live ? -4 : 1;
            thumbnailNumber = thumbnailNumber + n > 0 ? thumbnailNumber + n : 0;
        }
        const maxThumbnailNumber = Math.floor((end / 1000) / 10) - 9;
        const roundedPosition = end && playerInstance?.live ?
            Math.min(thumbnailNumber, maxThumbnailNumber) : thumbnailNumber;
        if (
            spritesheet
            && spritesheet?.baseUrl
            && spritesheet?.sprites?.length > roundedPosition
            && spritesheet?.sprites[roundedPosition].file
        ) {
            loadedThumbnail = {
                'start': (roundedPosition * 10000),
                'end': (roundedPosition * 10000) + 10000,
                'width': spritesheet.spriteWidth,
                'height': spritesheet.spriteHeight,
                'imageHeight': 900,
                'imageWidth': 960,
                'url': imagesBase64[spritesheet.baseUrl + spritesheet.sprites[roundedPosition].file],
                'x': spritesheet.sprites[roundedPosition].x,
                'y': spritesheet.sprites[roundedPosition].y
            };
        } else if (spritesheet
            && spritesheet?.baseUrl
            && spritesheet?.sprites?.length === roundedPosition
            && spritesheet?.sprites[roundedPosition - 1].file) {
            const roundedPosition2 = roundedPosition - 1;
            loadedThumbnail = {
                'start': (roundedPosition2 * 10000),
                'end': (roundedPosition2 * 10000) + 10000,
                'width': spritesheet.spriteWidth,
                'height': spritesheet.spriteHeight,
                'imageHeight': 900,
                'imageWidth': 960,
                'url': imagesBase64[spritesheet.baseUrl + spritesheet.sprites[roundedPosition2].file],
                'x': spritesheet.sprites[roundedPosition2].x,
                'y': spritesheet.sprites[roundedPosition2].y
            };
        }

        if (!loadedThumbnail) {
            setNextThumbnail(null);
        } else {
            setNextThumbnail(loadedThumbnail);
        }

    }, [playerInstance, spritesheet, position, item, imagesBase64]);

    useEffect(() => {
        // no thumbnail desired
        if (
            !playerInstance ||
            !Number.isFinite(position)
            || item?.data?.thumbnailsEnabled !== true
        ) return;
        // active thumbnail is still usable

        let length;
        if (typeof item?.getEndTime === 'function' && typeof item?.getStartTime === 'function') {
            length = item?.getEndTime() - item?.getStartTime();
        } else if (typeof item?.getDuration === 'function') {
            length = item?.getDuration() * 1000;
        } else {
            return;
        }
        if (
            nextThumbnail &&
            Number.isFinite(nextThumbnail.start) &&
            Number.isFinite(nextThumbnail.end) &&
            nextThumbnail.start <= position * length &&
            nextThumbnail.end > position * length
        ) {
            return;
        }
        doLoadThumbnail();
    }, [progress, position, item]);

    const canShowThumbnail =
        isVisible && Number.isFinite(position) && progress >= 0 && progress <= 100 && nextThumbnail;
    const activeThumbnail = canShowThumbnail ? nextThumbnail : undefined;

    const [containerWidth, containerHeight] = useMemo(() => {
        if (Device.isWeb) return [0, 0];
        let maxWidth = styles.width ?? CONTAINER_WIDTH;
        let maxHeight = styles.height ?? CONTAINER_HEIGHT;

        // find reference size for aspect ratio
        const { imageWidth, imageHeight } = activeThumbnail ?? {};
        let { width = imageWidth, height = imageHeight } = activeThumbnail ?? {};
        if (!width || !height) {
            const selectedVideoTrack = playerInstance?.selectedVideoTrack;
            width = selectedVideoTrack?.width;
            height = selectedVideoTrack?.height;
        }
        if (!width || !height) {
            width = 16;
            height = 9;
        }

        // adjust maximum container size to match aspect ratio
        if (width && height) {
            maxHeight = Math.min(maxHeight, Math.round((maxWidth / width) * height));
            maxWidth = Math.min(maxWidth, Math.round((maxHeight / height) * width));
        }

        return [maxWidth, maxHeight];
    }, [activeThumbnail]);

    const left = Number.isFinite(progress) ? `${progress}%` : '0px';

    if (
        !canShowThumbnail
        || item?.data?.thumbnailsEnabled !== true
    ) {
        return <></>;
    }
    return Device.isWeb ?
        <ThumbnailContainer
            left={left}
            isVisible={canShowThumbnail}
            data-test="Thumbnail-Container"
            className={`${className} Thumbnail-Container`}
            // scale={scale}
            activeThumbnail={activeThumbnail}
        /> : <ThumbnailAnchor
            data-test="Thumbnail-Anchor"
            className={className}
            width={containerWidth}
            left={left}
        >
            <ThumbnailContainer
                data-test="Thumbnail-Container"
                width={containerWidth}
                height={containerHeight}
                borderRadius={styles.borderRadius}
            >
                {activeThumbnail && <ThumbnailImage
                    activeThumbnail={activeThumbnail}
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                />}
            </ThumbnailContainer>
        </ThumbnailAnchor>
        ;
};
